import * as React from 'react'

import {
  SettingsEmployeeFoldersPageHeaderDocument,
  SettingsInternalFoldersPageHeaderDocument,
  SettingsPublicFoldersPageHeaderDocument,
  SettingsPublicFoldersPageHeaderQuery,
  SettingsRouteDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { MetaContext } from 'modules/core/lib/navigation/routes'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const RootPage = React.lazy(() => import('modules/documents/pages/settings/RootPage'))

const CustomFieldsFolderSettingsPage = React.lazy(
  () => import('modules/documents/components/Settings/pages/CustomFieldsFolderSettingsPage')
)

const EmployeesFolderSettingPage = React.lazy(
  () => import('modules/documents/components/Settings/pages/EmployeesFolderSettingsPage')
)

const InternalFolderSettingPage = React.lazy(
  () => import('modules/documents/components/Settings/pages/InternalFolderSettingsPage')
)

const PublicCompanyFolderSettingPage = React.lazy(
  () => import('modules/documents/components/Settings/pages/PublicCompanyFolderSettingsPage')
)

const RedirectToFolderSettingsPage = React.lazy(
  () => import('modules/documents/components/Settings/pages/RedirectToFolderSettingsPage')
)

const EmployeeFolderIndexPage = React.lazy(
  () => import('modules/documents/pages/settings/EmployeeFolderIndexPage')
)
const InternalFolderIndexPage = React.lazy(
  () => import('modules/documents/pages/settings/InternalFolderIndexPage')
)

const PublicCompanyFolderIndexPage = React.lazy(
  () => import('modules/documents/pages/settings/PublicCompanyFolderIndexPage')
)
const PublicCompanyFolderIndexPageHeader = React.lazy(
  () =>
    import('modules/documents/pages/settings/PublicCompanyFolderIndexPage/PublicCompanyFolderIndexPageHeader')
)
const InternalFolderIndexPageHeader = React.lazy(
  () =>
    import('modules/documents/pages/settings/InternalFolderIndexPage/InternalFolderIndexPageHeader')
)
const EmployeeFolderIndexPageHeader = React.lazy(
  () =>
    import('modules/documents/pages/settings/EmployeeFolderIndexPage/EmployeeFolderIndexPageHeader')
)

type FolderAndAncestorsDocumentType = typeof SettingsPublicFoldersPageHeaderDocument

type Folder =
  SettingsPublicFoldersPageHeaderQuery['documents']['foldersConnection']['edges'][number]['node']

const NewFolderModalPage = React.lazy(
  () => import('modules/documents/pages/settings/NewFolderModalPage')
)

const getFolderMeta =
  (
    query: FolderAndAncestorsDocumentType,
    navigationTo: (id: number) => string,
    buildHeaderComponent: (folder: NonNullable<Folder>) => React.ReactNode
  ) =>
  ({ params, watchQuery }: MetaContext) => {
    if (!params['id']) return { title: '' }
    return watchQuery({
      query,
      variables: {
        id: Number(params['id']),
      },
    }).map(({ data }) => {
      const folder = data?.documents?.foldersConnection?.edges[0]?.node
      const title = folder ? folder.name : ''

      const ancestors = data?.documents?.ancestors?.edges
        ?.map((edge) => edge.node)
        ?.map((node) => ({
          id: node.id,
          title: node.name,
          to: navigationTo(node.id),
        }))

      return {
        title,
        ancestors,
        headerComponent: folder ? buildHeaderComponent(folder) : undefined,
      }
    })
  }

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.folders.path,
      element: <RootPage />,
      handle: {
        resource: true,
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: SettingsRouteDocument,
            variables: {},
          }).map(({ data }) => {
            return {
              title: i18n.t('company_settings.general.documents.title'),
              subNavigation: [
                {
                  title: i18n.t('settings.folders.tabs.public_company'),
                  href: navigation.settings.folders.public.fullPath(),
                  isVisible: data?.documents?._policies?.companyManagePublicCompanyFolders.can,
                  index: true,
                },
                {
                  title: i18n.t('settings.folders.tabs.internal'),
                  href: navigation.settings.folders.internal.fullPath(),
                  isVisible: data?.documents?._policies?.companyManageFolders.can,
                },
                {
                  title: i18n.t('settings.folders.tabs.employee'),
                  href: navigation.settings.folders.employees.fullPath(),
                  isVisible: data?.documents?._policies?.companyManageEmployeesFolders.can,
                },
                {
                  title: i18n.t('settings.folders.tabs.custom_fields'),
                  href: navigation.settings.folders.customFields.fullPath(),
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToFolderSettingsPage />,
        },
        {
          path: navigation.settings.folders.internal.path,
          handle: {
            meta: () => ({
              title: i18n.t('settings.folders.tabs.internal'),
            }),
          },
          children: [
            {
              path: '',
              element: <InternalFolderSettingPage />,
              children: [
                {
                  path: navigation.settings.folders.internal.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.folders.internal.show.path,
              element: <InternalFolderIndexPage />,
              handle: {
                resource: true,
                meta: getFolderMeta(
                  SettingsInternalFoldersPageHeaderDocument,
                  (id) => navigation.settings.folders.internal.show.fullPath({ id }),
                  (folder) => <InternalFolderIndexPageHeader folder={folder} />
                ),
              },
              children: [
                {
                  path: navigation.settings.folders.public.show.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.folders.public.path,
          handle: {
            meta: () => ({
              title: i18n.t('settings.folders.tabs.public_company'),
            }),
          },
          children: [
            {
              path: '',
              element: <PublicCompanyFolderSettingPage />,
              children: [
                {
                  path: navigation.settings.folders.public.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.folders.public.show.path,
              element: <PublicCompanyFolderIndexPage />,
              handle: {
                resource: true,
                meta: getFolderMeta(
                  SettingsPublicFoldersPageHeaderDocument,
                  (id) => navigation.settings.folders.public.show.fullPath({ id }),
                  (folder) => <PublicCompanyFolderIndexPageHeader folder={folder} />
                ),
              },
              children: [
                {
                  path: navigation.settings.folders.public.show.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.folders.employees.path,
          handle: {
            meta: () => ({
              title: i18n.t('settings.folders.tabs.employee'),
            }),
          },
          children: [
            {
              path: '',
              element: <EmployeesFolderSettingPage />,
              children: [
                {
                  path: navigation.settings.folders.employees.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.folders.employees.show.path,
              element: <EmployeeFolderIndexPage />,
              handle: {
                resource: true,
                meta: getFolderMeta(
                  SettingsEmployeeFoldersPageHeaderDocument,
                  (id) => navigation.settings.folders.employees.show.fullPath({ id }),
                  (folder) => <EmployeeFolderIndexPageHeader folder={folder} />
                ),
              },
              children: [
                {
                  path: navigation.settings.folders.public.show.new.path,
                  element: <NewFolderModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.folders.customFields.path,
          handle: {
            meta: () => ({
              title: i18n.t('settings.folders.tabs.custom_fields'),
            }),
          },
          element: <CustomFieldsFolderSettingsPage />,
        },
      ],
    },
  ]
}

export default settingsRoutes
